/**
 * Stable ticker handoff contract between Strategy Tools (/ticker/:symbol)
 * and Ticker Intelligence Workspace (/ticker/:symbol/intelligence).
 *
 * Canonical family ids are Workbench values only. Mock UI family names
 * (income, protection, volatility, repair) must never be written to URLs.
 */

export const CANONICAL_FAMILIES = ["premium_selling", "long_vol", "equity"];

export const FAMILY_LABELS = {
  premium_selling: "Premium-Selling",
  long_vol: "Long-Vol",
  equity: "Equity",
};

/** Intelligence URL tab ids. Internal Workspace panels still use `talk`. */
export const INTELLIGENCE_TABS = [
  "overview",
  "regime",
  "options",
  "technicals",
  "fundamentals",
  "earnings",
  "ticker-talk",
  "ownership",
];

const WORKSPACE_TAB_TO_URL = { talk: "ticker-talk" };
const URL_TAB_TO_WORKSPACE = { "ticker-talk": "talk" };

const EXPIRY_RE = /^(\d{4})-(\d{2})-(\d{2})$/;
const DTE_MAX = 3650;
const STRATEGY_RE = /^[A-Za-z0-9][A-Za-z0-9_-]{0,63}$/;

export function parseExpiry(raw) {
  if (raw == null || raw === "") return null;
  const s = String(raw).trim();
  const m = s.match(EXPIRY_RE);
  if (!m) return null;
  const year = Number(m[1]);
  const month = Number(m[2]);
  const day = Number(m[3]);
  const dt = new Date(Date.UTC(year, month - 1, day));
  if (
    dt.getUTCFullYear() !== year
    || dt.getUTCMonth() !== month - 1
    || dt.getUTCDate() !== day
  ) return null;
  return s;
}

export function parseDte(raw) {
  if (raw == null || raw === "") return null;
  const s = String(raw).trim();
  if (!/^\d+$/.test(s)) return null;
  const n = Number(s);
  if (!Number.isInteger(n) || n < 0 || n > DTE_MAX) return null;
  return n;
}

export function parseFamily(raw) {
  if (raw == null || raw === "") return null;
  const s = String(raw).trim();
  return CANONICAL_FAMILIES.includes(s) ? s : null;
}

export function parseStrategy(raw) {
  if (raw == null || raw === "") return null;
  const s = String(raw).trim();
  return STRATEGY_RE.test(s) ? s : null;
}

export function parseIntelligenceTab(raw) {
  if (raw == null || raw === "") return "overview";
  const s = String(raw).trim();
  return INTELLIGENCE_TABS.includes(s) ? s : "overview";
}

export function toWorkspaceTab(urlTab) {
  const tab = parseIntelligenceTab(urlTab);
  return URL_TAB_TO_WORKSPACE[tab] || tab;
}

export function toIntelligenceTab(workspaceTab) {
  if (!workspaceTab) return "overview";
  const mapped = WORKSPACE_TAB_TO_URL[workspaceTab] || workspaceTab;
  return INTELLIGENCE_TABS.includes(mapped) ? mapped : "overview";
}

export function utcToday() {
  const now = new Date();
  const y = now.getUTCFullYear();
  const m = String(now.getUTCMonth() + 1).padStart(2, "0");
  const d = String(now.getUTCDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

export function calendarDte(expiry, asOfDate) {
  const exp = parseExpiry(expiry);
  const asOf = parseExpiry(asOfDate) || utcToday();
  if (!exp) return null;
  const ms = Date.parse(`${exp}T00:00:00Z`) - Date.parse(`${asOf}T00:00:00Z`);
  if (!Number.isFinite(ms)) return null;
  return Math.max(0, Math.round(ms / 86400000));
}

function expirationDate(entry) {
  if (entry == null) return null;
  if (typeof entry === "string") return parseExpiry(entry);
  return parseExpiry(entry.date || entry.expiry);
}

function expirationDte(entry) {
  if (entry == null || typeof entry === "string") return null;
  const n = Number(entry.dte);
  return Number.isFinite(n) ? n : null;
}

export function dteFromExpiry(expiry, expirations, asOfDate) {
  const exp = parseExpiry(expiry);
  if (!exp) return null;
  const list = Array.isArray(expirations) ? expirations : [];
  const hit = list.find((e) => expirationDate(e) === exp);
  const listed = expirationDte(hit);
  if (listed != null && listed >= 0) return listed;
  return calendarDte(exp, asOfDate);
}

export function parseHandoffSearch(search) {
  const get = typeof search?.get === "function"
    ? (key) => search.get(key)
    : (key) => (search && search[key] != null ? search[key] : null);
  return {
    expiry: parseExpiry(get("expiry")),
    dte: parseDte(get("dte")),
    family: parseFamily(get("family")),
    strategy: parseStrategy(get("strategy")),
    tab: parseIntelligenceTab(get("tab")),
  };
}

export function resolveHandoff(input, { asOfDate, expirations } = {}) {
  const expiry = parseExpiry(input?.expiry);
  const queryDte = parseDte(input?.dte);
  const family = parseFamily(input?.family);
  const strategy = parseStrategy(input?.strategy);
  const tab = parseIntelligenceTab(input?.tab);
  const dte = expiry
    ? dteFromExpiry(expiry, expirations, asOfDate)
    : queryDte;
  return { expiry, dte, family, strategy, tab };
}

export function buildHandoffSearch(input, { includeTab = false, asOfDate, expirations } = {}) {
  const resolved = resolveHandoff(input, { asOfDate, expirations });
  const params = new URLSearchParams();
  if (resolved.expiry) params.set("expiry", resolved.expiry);
  if (resolved.dte != null && resolved.dte <= DTE_MAX) params.set("dte", String(resolved.dte));
  if (resolved.family) params.set("family", resolved.family);
  if (resolved.strategy) params.set("strategy", resolved.strategy);
  if (includeTab && resolved.tab && resolved.tab !== "overview") params.set("tab", resolved.tab);
  return params;
}

export function intelligencePath(symbol, input, opts) {
  const sym = String(symbol || "").toUpperCase();
  const qs = buildHandoffSearch(input, { ...opts, includeTab: true }).toString();
  return qs ? `/ticker/${sym}/intelligence?${qs}` : `/ticker/${sym}/intelligence`;
}

export function strategyToolsPath(symbol, input, opts) {
  const sym = String(symbol || "").toUpperCase();
  const qs = buildHandoffSearch(input, { ...opts, includeTab: false }).toString();
  return qs ? `/ticker/${sym}?${qs}` : `/ticker/${sym}`;
}

export function findRankedStrategy(strategy, ranked) {
  const id = parseStrategy(strategy);
  if (!id) return null;
  const rows = Array.isArray(ranked?.ranked_strategies) ? ranked.ranked_strategies : [];
  return rows.find((row) => row && row.strategy_id === id) || null;
}

/** Ranked-row identity only. No wing, ranked[0], or static-registry fallback. */
export function lookupStrategyId(strategy, ranked) {
  const row = findRankedStrategy(strategy, ranked);
  return row ? row.strategy_id : null;
}

export function applyResearchFocusSearch(parsed, { strategyId, family }, extras = {}) {
  return buildHandoffSearch({
    expiry: parsed?.expiry,
    dte: parsed?.dte,
    family: parseFamily(family),
    strategy: strategyId,
    tab: "options",
  }, { includeTab: true, ...extras });
}

export function formatResearchHandoffCopy(researchFocus) {
  const kind = researchFocus?.kind;
  if (!kind || kind === "choose") {
    return {
      primary: "Choose a strategy to view historical Backtest context.",
      secondary: null,
    };
  }
  if (kind === "pending") {
    return {
      primary: "Loading historical strategy focus.",
      secondary: null,
    };
  }
  if (kind === "unavailable") {
    return {
      primary: "Historical strategy focus unavailable.",
      secondary: "The requested strategy is not available in the current historical dataset.",
    };
  }
  return {
    primary: researchFocus.displayName
      ? `Research focus: ${researchFocus.displayName}`
      : "Research focus unavailable.",
    secondary: researchFocus.familyDisplay
      ? `Registry family: ${researchFocus.familyDisplay}`
      : null,
  };
}
